import { briefProfilePlanIdentity } from "./brief-profile-plan-identity.mjs";
const COUNTRIES = new Set(["US", "IN", "GB", "CA", "AU", "DE", "FR", "JP"]);
const LANGUAGES = new Set([
  "en",
  "zh",
  "hi",
  "es",
  "ar",
  "fr",
  "bn",
  "pt",
  "id",
  "gu",
  "ur",
  "ru",
  "de",
  "ja",
  "pcm",
  "arz",
  "mr",
  "vi",
  "te",
  "sw",
  "ha",
  "tr",
  "pnb",
  "tl",
  "ta",
  "yue",
  "kn",
  "ml",
  "or",
  "as",
  "pa",
]);
const SYNTHETIC = new Set(["editorial_framing", "editorial_takeaway"]);
const BOUNDS = {
  runId: [1, 128],
  localDate: [10, 10],
  marketCountry: [2, 2],
  language: [2, 3],
  profileId: [1, 256],
  planIdentity: [1, 8192],
  sectionId: [1, 128],
  itemId: [0, 4096],
  edition: [0, 64],
};
const SHA_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];
const rotate = (value, bits) => (value >>> bits) | (value << (32 - bits));
/** Synchronous SHA-256 is used solely for the published native cursor identity,
 * so a navigation gesture can produce canonical metadata without an async race. */
export function portableCursorSha256(value) {
  const bytes = new TextEncoder().encode(value);
  const padded = new Uint8Array(Math.ceil((bytes.length + 9) / 64) * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bytes.length / 0x20000000));
  view.setUint32(padded.length - 4, (bytes.length * 8) >>> 0);
  const hash = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];
  const words = new Uint32Array(64);
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let index = 0; index < 16; index++) words[index] = view.getUint32(offset + index * 4);
    for (let index = 16; index < 64; index++) {
      const a = words[index - 15],
        b = words[index - 2];
      words[index] =
        (words[index - 16] +
          (rotate(a, 7) ^ rotate(a, 18) ^ (a >>> 3)) +
          words[index - 7] +
          (rotate(b, 17) ^ rotate(b, 19) ^ (b >>> 10))) >>>
        0;
    }
    let [a, b, c, d, e, f, g, h] = hash;
    for (let index = 0; index < 64; index++) {
      const first =
        (h +
          (rotate(e, 6) ^ rotate(e, 11) ^ rotate(e, 25)) +
          ((e & f) ^ (~e & g)) +
          SHA_K[index] +
          words[index]) >>>
        0;
      const second =
        ((rotate(a, 2) ^ rotate(a, 13) ^ rotate(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + first) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (first + second) >>> 0;
    }
    for (const [index, value] of [a, b, c, d, e, f, g, h].entries())
      hash[index] = (hash[index] + value) >>> 0;
  }
  return hash.map((value) => value.toString(16).padStart(8, "0")).join("");
}
function text(value, min, max) {
  return (
    typeof value === "string" &&
    value.length >= min &&
    value.length <= max &&
    !/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/u.test(value)
  );
}
const integer = (value, min, max) => Number.isSafeInteger(value) && value >= min && value <= max;
const dayKey = (value) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const contentIds = (ids) => ids.filter((id) => !SYNTHETIC.has(id));
export function decodePortableBriefCursor(raw, mode, now = Date.now()) {
  if (typeof raw !== "string" || !raw || raw.length > 24000 || !["classic", "reels"].includes(mode))
    return null;
  let value;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (
    !value ||
    Array.isArray(value) ||
    value.version !== 1 ||
    value.mode !== mode ||
    typeof value.completed !== "boolean"
  )
    return null;
  if (
    Object.entries(BOUNDS).some(([key, [min, max]]) => !text(value[key], min, max)) ||
    !value.runId.trim() ||
    !COUNTRIES.has(value.marketCountry) ||
    !LANGUAGES.has(value.language)
  )
    return null;
  const day = Date.parse(`${value.localDate}T00:00:00.000Z`);
  if (
    !Number.isFinite(day) ||
    new Date(day).toISOString().slice(0, 10) !== value.localDate ||
    !integer(value.startedAtEpochMs, 1, now + 300000) ||
    !integer(value.updatedAtEpochMs, value.startedAtEpochMs, now + 300000)
  )
    return null;
  const ids = value.sectionIds;
  if (
    !Array.isArray(ids) ||
    ids.length < 1 ||
    ids.length > 64 ||
    new Set(ids).size !== ids.length ||
    ids.some(
      (id) =>
        typeof id !== "string" ||
        !/^[a-z0-9_-]{1,128}$/.test(id) ||
        ["finance", "productivity"].includes(id) ||
        (mode === "reels" && SYNTHETIC.has(id)),
    ) ||
    !contentIds(ids).length ||
    !ids.includes(value.sectionId)
  )
    return null;
  if (
    !integer(value.ordinalWithinSection, 1, 1000) ||
    !integer(value.pageIndex, 0, mode === "classic" ? ids.length - 1 : 10000) ||
    (mode === "classic" && ids[value.pageIndex] !== value.sectionId) ||
    !integer(value.completedCount, 0, ids.length) ||
    !integer(value.readMinutes, 0, 240) ||
    (mode === "reels" && !value.itemId.trim())
  )
    return null;
  return {
    version: 1,
    mode,
    ...Object.fromEntries(Object.keys(BOUNDS).map((key) => [key, value[key]])),
    startedAtEpochMs: value.startedAtEpochMs,
    updatedAtEpochMs: value.updatedAtEpochMs,
    sectionIds: [...ids],
    ordinalWithinSection: value.ordinalWithinSection,
    pageIndex: value.pageIndex,
    completedCount: value.completedCount,
    completed: value.completed,
    readMinutes: value.readMinutes,
  };
}
export function canonicalPortableBriefCursor(value) {
  return JSON.stringify(
    Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, value[key]]),
    ),
  );
}
export function mergePortableBriefCursors(local, remote, mode, now = Date.now()) {
  const a = decodePortableBriefCursor(local, mode, now),
    b = decodePortableBriefCursor(remote, mode, now);
  if (!a) return b ? canonicalPortableBriefCursor(b) : "";
  if (!b) return canonicalPortableBriefCursor(a);
  return b.updatedAtEpochMs > a.updatedAtEpochMs ||
    (b.updatedAtEpochMs === a.updatedAtEpochMs &&
      canonicalPortableBriefCursor(b) > canonicalPortableBriefCursor(a))
    ? canonicalPortableBriefCursor(b)
    : canonicalPortableBriefCursor(a);
}
export function portableBriefPlanIdentity(context) {
  if (!context.profile?.id || !["classic", "reels"].includes(context.mode)) return null;
  if (context.mode === "classic") return briefProfilePlanIdentity(context.profile, context.country);
  const parts = [
    "reels-v1",
    context.profile.id,
    context.country,
    context.language,
    ...contentIds(context.sectionIds),
  ];
  return portableCursorSha256(
    parts.map((part) => `${new TextEncoder().encode(part).length}:${part}`).join(""),
  );
}
function edition(length) {
  return length === "quick" ? "QUICK" : length === "deep" ? "DEEP" : "STANDARD";
}
export function matchingPortableBriefCursor(raw, context, now = Date.now()) {
  const cursor = decodePortableBriefCursor(raw, context.mode, now);
  const ids = contentIds(context.sectionIds);
  return cursor &&
    cursor.marketCountry === context.country &&
    cursor.language === context.language &&
    cursor.profileId === context.profile.id &&
    cursor.planIdentity === portableBriefPlanIdentity(context) &&
    JSON.stringify(contentIds(cursor.sectionIds)) === JSON.stringify(ids) &&
    cursor.startedAtEpochMs <= now &&
    cursor.localDate === dayKey(now) &&
    dayKey(cursor.startedAtEpochMs) === dayKey(now) &&
    (context.mode !== "classic" || cursor.edition === edition(context.profile.preferredLength))
    ? cursor
    : null;
}
function sourceUrl(value) {
  try {
    const url = new URL(value);
    return /^https?:$/.test(url.protocol) && !url.username && !url.password && value.length <= 4096
      ? value.trim()
      : "";
  } catch {
    return "";
  }
}
const reelId = (id, key) => `${id}:${portableCursorSha256(key).slice(0, 24)}`;
export function makePortableBriefCursor(input, now = Date.now()) {
  const ids = contentIds(input.sectionIds),
    sectionId =
      input.sectionId === "editorial_framing"
        ? ids[0]
        : input.sectionId === "editorial_takeaway"
          ? ids.at(-1)
          : input.sectionId;
  const index = ids.indexOf(sectionId);
  if (index < 0) return null;
  const url = sourceUrl(input.itemUrl || "");
  const itemId = input.mode === "reels" || !url ? reelId(sectionId, url || sectionId) : url;
  const value = {
    version: 1,
    mode: input.mode,
    runId: input.runId,
    startedAtEpochMs: input.startedAtEpochMs,
    updatedAtEpochMs: input.updatedAtEpochMs,
    localDate: dayKey(input.startedAtEpochMs),
    marketCountry: input.country,
    language: input.language,
    profileId: input.profile.id,
    planIdentity: portableBriefPlanIdentity(input),
    sectionIds: ids,
    sectionId,
    itemId,
    ordinalWithinSection: input.mode === "classic" ? 1 : Math.max(1, input.articleOrdinal + 1),
    pageIndex: input.mode === "classic" ? index : (input.pageIndex ?? index),
    completedCount: input.completed ? ids.length : index,
    completed: input.completed === true,
    readMinutes: Math.min(240, Math.max(0, Math.round(input.readMinutes || 0))),
    edition: input.mode === "classic" ? edition(input.profile.preferredLength) : "",
  };
  return decodePortableBriefCursor(JSON.stringify(value), input.mode, now);
}
export function portableCursorArticleOrdinal(cursor, articles) {
  const index = articles.findIndex((article) => {
    const url = sourceUrl(article.originalArticleUrl || article.link || article.url || "");
    return url && (cursor.itemId === url || cursor.itemId === reelId(cursor.sectionId, url));
  });
  return Math.min(
    Math.max(0, index >= 0 ? index : cursor.ordinalWithinSection - 1),
    Math.max(0, articles.length - 1),
  );
}
